import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Popover, CheckBoxField, ActionSelect } from '@unite-us/app-components';
import StatusSelect from 'src/components/StatusSelect';
import { Spinner } from 'common/spinners';
import ClientProfileLink from 'src/components/ClientSummary/components/ClientProfileLink';
import { browserHistory } from 'common/utils/browserHistory';
import { fetchClientRelationship, updateClientRelationship } from 'actions/CareCoordinator/Contact/Group';
import { isEmpty } from 'lodash';

const DrawerHeader = ({
  contact,
  fullContact,
  isFetchingFull,
  groupId,
  onClientNameClick,
}) => {
  const [clientRelationship, setClientRelationship] = useState({});
  const [isLoadingRelationship, setIsLoadingRelationship] = useState(false);
  const [isUpdatingRelationship, setIsUpdatingRelationship] = useState(false);
  const clientDataRef = useRef(null);

  useEffect(() => {
    if (contact) {
      clientDataRef.current = contact;
    }
  }, [contact]);

  const handleFindResources = useCallback(() => {
    const currentClientData = clientDataRef.current;

    if (!currentClientData?.id) {
      window.datadogLogs.logger.warn('Cannot find resources: client ID is not available.', currentClientData);
      return;
    }
    browserHistory.push(`/referrals/create/add-resources?person=${currentClientData.id}`);
  }, []);

  const getClientRelationship = async () => {
    try {
      setIsLoadingRelationship(true);
      const relationship = await fetchClientRelationship(contact.id, groupId);
      setClientRelationship(relationship);
    } catch (error) {
      window.datadogLogs.logger.error('Error fetching client relationship:', error);
    } finally {
      setIsLoadingRelationship(false);
    }
  };

  const handleOnCheck = async (e) => {
    try {
      setIsUpdatingRelationship(true);
      const relationship = await updateClientRelationship(clientRelationship, !!e.target.checked);
      setClientRelationship(relationship);
    } catch (error) {
      window.datadogLogs.logger.error('Error updating client relationship:', error);
    } finally {
      setIsUpdatingRelationship(false);
    }
  };

  const handleClientNameClick = () => {
    if (onClientNameClick) {
      onClientNameClick();
    } else {
      browserHistory.push(`/facesheet/${contact?.id}`);
    }
  };

  useEffect(() => {
    if (contact?.id && groupId) {
      getClientRelationship();
    }
  }, [contact?.id, groupId]);
  return (
    <div className="drawer-header">
      {/* Client Name and Profile Link */}
      <div className="flex flex-wrap justify-between items-center">
        <div className="flex flex-row flex-wrap items-center">
          <Button
            className="hover:underline text-xl px-0 mr-2"
            link
            onClick={handleClientNameClick}
            label={contact?.full_name}
          />
          <Popover
            id="popover-client-profile-link"
            content={<span>Copy link</span>}
            position="right"
            trigger="hover"
            width={16}
            className="py-2 px-1"
          >
            <ClientProfileLink linkToCopy={`${window.location.origin}/facesheet/${contact?.id}`}>
              <Icon icon="Link" size={16} color={'action-blue'} />
            </ClientProfileLink>
          </Popover>
        </div>
        <ActionSelect
          actions={[
            {
              action: handleFindResources,
              description: 'Share resources with your client or send referrals through Unite Us.',
              icon: 'Search',
              label: `Find Resources for ${contact?.first_name || 'this client'}`,
              value: 'find_resources',
            },
            // {
            //   action: () => {},
            //   description: 'Open an internal case for your client.',
            //   icon: 'Email',
            //   label: 'New Case',
            //   value: 'beans',
            // },
            // {
            //   action: () => {},
            //   description: 'Upload a document to the client’s face sheet.',
            //   icon: 'FolderOpen',
            //   label: 'Upload a Document',
            //   value: 'quick 2',
            // },
            // {
            //   action: () => {},
            //   description: 'Verify your client’s eligibility.',
            //   icon: 'Case',
            //   label: 'Verify Insurance',
            //   value: 'quick 2',
            // },
          ]}
          id="action-with-icons-and-helper-text"
          label="Action Select"
          labelKey="label"
          placeholder="Take Action"
          valueKey="value"
        />
      </div>

      {/* Status Select */}
      <div className="flex mb-2">
        {(fullContact && !isFetchingFull) ? (
          <StatusSelect contact={fullContact} isFetching={isFetchingFull} showCondensedView />
        ) : (
          <Spinner />
        )}
      </div>

      {/* Client Action Checkbox */}
      {!isEmpty(clientRelationship) && (
      <div className="flex items-center mb-2 sm:pl-1">
        {isLoadingRelationship ? (
          <div className="flex items-center">
            <Spinner />
          </div>
        ) : (
          <div className="flex items-center">
            <CheckBoxField
              id="client-requires-action__checkbox"
              className="client-requires-action__checkbox"
              label="Client requires action"
              checked={clientRelationship.flagged || false}
              onChange={handleOnCheck}
              disabled={isUpdatingRelationship}
              showError={false}
              showHint={false}
            />
            {isUpdatingRelationship && (
              <div className="ml-2">
                <Spinner />
              </div>
            )}
          </div>
        )}
      </div>
)}
    </div>
  );
};

DrawerHeader.propTypes = {
  contact: PropTypes.shape({
    id: PropTypes.string.isRequired,
    full_name: PropTypes.string.isRequired,
    first_name: PropTypes.string.isRequired,
  }).isRequired,
  fullContact: PropTypes.object,
  isFetchingFull: PropTypes.bool.isRequired,
  groupId: PropTypes.string.isRequired,
  onClientNameClick: PropTypes.func,
};

DrawerHeader.defaultProps = {
  fullContact: null,
  onClientNameClick: null,
};

export default DrawerHeader;
